'use client';
import { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SearchQueryParamsExt } from '@peerless/data-access/etilize-api-client';
import { productSearch } from '@peerless/data-access/next-api';
import { useCurrentLocale } from '@peerless/ui-locale';
import { useDebouncedCallback } from 'use-debounce';
import { debounceTime, noResults } from './types';

export type SearchCategory = 'mounts' | 'kiosks' | 'displays' | 'other';

// Minimum search requirement, can be overidden
const defaultParams: SearchQueryParamsExt = {
  pageSize: 4,
};

export interface EtilizeSearchOptions {
  category: SearchCategory;
}

export function useEtilizeSearch({ category }: EtilizeSearchOptions) {
  const { locale } = useCurrentLocale();
  const queryClient = useQueryClient();
  const [queryParams, setQueryParams] = useState<SearchQueryParamsExt | null>(null);

  const reset = useCallback(() => {
    setQueryParams(null);
    queryClient.resetQueries({
      queryKey: ['Etilize', 'Search', category, { ...defaultParams, ...queryParams, locale }],
    });
  }, [queryClient, category, queryParams, locale]);

  const { refetch, isError, isLoading, isRefetching, data, error } = useQuery({
    queryKey: ['Etilize', 'Search', category, { ...defaultParams, ...queryParams, locale }],
    queryFn: ({ signal }) => productSearch({ ...queryParams }, { signal }),
    enabled: !!queryParams,
  });

  const search = useDebouncedCallback((keyword: string) => {
    setQueryParams({
      ...defaultParams,
      keywordFilter: keyword,
      locale,
      category,
    });
  }, debounceTime);

  useEffect(() => {
    if (queryParams) {
      refetch();
    }
  }, [queryParams, refetch]);

  return {
    refetch,
    isError,
    isLoading,
    data: data ?? noResults(queryParams?.keywordFilter),
    error,
    search,
    reset,
    isRefetching,
  };
}
